import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from "react";

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Swal from "sweetalert2";

import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/DataTableContainer';

import { useDispatch, useSelector } from "react-redux";
import { fetchPromotionsData } from "../../store/promotions/actions";

const Promotions = props => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data, loading, error } = useSelector(state => state.Promotions);

  useEffect(() => {
    dispatch(fetchPromotionsData());
  }, [dispatch]);

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("authToken");
          const response = await fetch(
            `${process.env.REACT_APP_WEB_APP_URL}/api/web/promotions/delete/${id}`,
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const result = await response.json();

          if (response.status === 200) {
            toast.success('Promotion has been deleted.', { autoClose: 3000 });
            dispatch(fetchPromotionsData());
          } else {
            toast.error(result?.message || 'Failed to delete', { autoClose: 3000 });
          }
        } catch (err) {
          console.error(err);
          toast.error("Failed to delete protion", { autoClose: 3000 });
        }
      }
    });
  };

  const handleRestore = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f8b425",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, restore it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("authToken");
          const response = await fetch(
            `${process.env.REACT_APP_WEB_APP_URL}/api/web/promotions/restore/${id}`,
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const result = await response.json();

          if (response.status === 200) {
            toast.success('Promotion has been restored.', { autoClose: 3000 });
            dispatch(fetchPromotionsData());
          } else {
            toast.error(result?.message || 'Failed to restored', { autoClose: 3000 });
          }
        } catch (err) {
          console.error(err);
          toast.error("Failed to restored promotion", { autoClose: 3000 });
        }
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        header: "Image",
        accessorKey: "image",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <img
            src={row.original.image || "assets/images/no-image.png"}
            alt="Image"
            style={{ width: 40, height: 40, borderRadius: "50%" }}
          />
        )
      },
      {
        header: 'Name',
        accessorKey: 'title',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Start Date',
        accessorKey: 'start_date',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'End Date',
        accessorKey: 'end_date',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Status',
        accessorKey: 'status',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Action',
        id: 'action',
        cell: ({ row }) => {
          const promotionsId = row.original?.id;
          const is_deleted = row.original?.is_deleted;
          return (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-success"
                onClick={() => navigate(`/promotions/edit/${promotionsId}`)}
              >
                <i className="mdi mdi-pencil"></i> Edit
              </button>
              {is_deleted == 1 ? (
                <button
                  className="btn btn-sm btn-warning"
                  onClick={() => handleRestore(promotionsId)}
                >
                  <i className="mdi mdi-restore"></i> Restore
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(promotionsId)}
                >
                  <i className="mdi mdi-delete"></i> Delete
                </button>
              )}
            </div>
          );
        }
      }
    ],
    []
  );

  const totalRecords = data?.data?.length ?? 0;

  const transformedData = useMemo(() => {
    return data?.data?.map(item => {
      return {
        id: item.id,
        image: item.image || "assets/images/no-image.png",
        title: item.title || "N/A",
        start_date: item.start_date || "N/A",
        end_date: item.end_date || "N/A",
        status: item.status || "N/A",
        ...item
      };
    });
  }, [data]);

  document.title = "Promotions | Moeen";
  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Home" breadcrumbItem="Promotions" />
        <div className="float-end d-none d-md-block">
          <Link to="/promotions/create"
            color="primary"
            className="btn btn-primary text-white waves-effect waves-light"
          >
            Add Promotion
          </Link>
        </div>
        {loading ? (
          <Skeleton count={10} height={40} />
        ) : (
          <TableContainer
            columns={columns}
            data={transformedData || []}
            isGlobalFilter={true}
            isPagination={true}
            SearchPlaceholder={`Looking for something? ${totalRecords} results loaded.`}
            pagination="pagination"
            paginationWrapper='dataTables_paginate paging_simple_numbers'
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
        )}
      </div>
    </div>
  );
};

Promotions.propTypes = {
  t: PropTypes.any
};

export default Promotions;